import { Component, ElementRef, ViewChild } from '@angular/core';
import { FormControl, FormGroup, Validators } from '@angular/forms';
import { DynamicDialogConfig, DynamicDialogRef } from 'primeng/dynamicdialog';
import { UserAreaModel } from '../../../models/user-area.model';
import { isValidFileType } from '../../../utils/file.utils';

export interface BulkUploadMemberAreasDialogResult {
  file: File;
  areaIds: number[];
}

@Component({
  selector: 'app-bulk-upload-member-areas-dialog',
  templateUrl: './bulk-upload-member-areas-dialog.component.html',
  styleUrl: './bulk-upload-member-areas-dialog.component.scss',
  standalone: false,
})
export class BulkUploadMemberAreasDialogComponent {
  @ViewChild('fileInput') private fileInput?: ElementRef<HTMLInputElement>;

  public memberAreas: UserAreaModel[] = [];
  public selectedFile: File | null = null;
  public fileError = '';

  public form = new FormGroup({
    areaIds: new FormControl<number[]>([], Validators.required),
    fileName: new FormControl<string>('', Validators.required),
  });

  constructor(
    private dialogRef: DynamicDialogRef,
    private config: DynamicDialogConfig
  ) {
    this.memberAreas = this.config.data?.memberAreas ?? [];
  }

  public triggerFileSelect(): void {
    this.fileInput?.nativeElement.click();
  }

  public onFileSelected(event: Event): void {
    const input = event.target as HTMLInputElement;
    const file = input.files?.[0] ?? null;
    if (!file) {
      this.selectedFile = null;
      this.fileError = '';
      this.form.patchValue({ fileName: '' });
      return;
    }

    if (!isValidFileType(file)) {
      this.selectedFile = null;
      this.fileError =
        'Ungültiger Dateityp. Bitte lade eine gültige Datei hoch.';
      this.form.patchValue({ fileName: '' });
      return;
    }

    this.selectedFile = file;
    this.fileError = '';
    this.form.patchValue({ fileName: file.name });
  }

  public closeDialog(): void {
    this.dialogRef.close();
  }

  public submit(): void {
    this.form.markAllAsTouched();
    if (this.form.invalid || !this.selectedFile) return;

    const fileName = (this.form.value.fileName ?? '').trim();
    const fileForUpload = this.createRenamedFile(this.selectedFile, fileName);

    this.dialogRef.close({
      file: fileForUpload,
      areaIds: this.form.value.areaIds ?? [],
    } as BulkUploadMemberAreasDialogResult);
  }

  private createRenamedFile(originalFile: File, requestedName: string): File {
    if (!requestedName || requestedName === originalFile.name) {
      return originalFile;
    }

    const originalExtension = this.getExtension(originalFile.name);
    const requestedExtension = this.getExtension(requestedName);
    const normalizedName =
      !requestedExtension && originalExtension
        ? `${requestedName}.${originalExtension}`
        : requestedName;

    return new File([originalFile], normalizedName, {
      type: originalFile.type,
      lastModified: originalFile.lastModified,
    });
  }

  private getExtension(fileName: string): string {
    const parts = fileName.split('.');
    if (parts.length < 2) return '';
    return parts.pop()?.trim() ?? '';
  }
}
